import produce from "immer";
import { Dispatch } from "react";
import { CustomizationTrack } from "../../components/organisms/customization/CustomizationHeader";
import { Content } from "../../types/customization/content";
import { Template } from "../../types/customization/template";
import { NotificationByCode } from "../../types/notification";
import { SelectionPhoto, SelectionState } from "../../types/selection/selection";
import {
  addNotificationByCode,
  AddNotificationByCodeAction,
} from "./actions/addNotificationByCode";
import {
  addOrUpdateSelectionPhoto,
  AddOrUpdateSelectionPhotoAction,
} from "./actions/addOrUpdateSelectionPhoto";
import { addPage, AddPageAction } from "./actions/addPage";
import {
  addPageAndSwapEditablePictureElementPicture,
  AddPageAndSwapEditablePictureElementPictureAction,
} from "./actions/addPageAndSwapEditablePictureElementPicture";
import { addPicture, AddPictureAction } from "./actions/addPicture";
import {
  addPicturesWithAutofill,
  AddPicturesWithAutofillAction,
} from "./actions/addPicturesWithAutofill";
import {
  changeAllPagesColorDefinition,
  ChangeAllPagesColorDefinitionAction,
} from "./actions/changeAllPagesColorDefinition";
import {
  changeAllPagesFontDefinition,
  ChangeAllPagesFontDefinitionAction,
} from "./actions/changeAllPagesFontDefinition";
import {
  changeAllPagesPageDefinition,
  ChangeAllPagesPageDefinitionAction,
} from "./actions/changeAllPagesPageDefinition";
import { changeBookNumber, ChangeBookNumberAction } from "./actions/changeBookNumber";
import { changeBookTitle, ChangeBookTitleAction } from "./actions/changeBookTitle";
import {
  changeBookTitleFontKey,
  ChangeBookTitleFontKeyAction,
} from "./actions/changeBookTitleFontKey";
import {
  changeEditablePictureElementCroppingAndFilter,
  ChangeEditablePictureElementCroppingAndFilterAction,
} from "./actions/changeEditablePictureElementCroppingAndFilter";
import {
  changeEditablePictureElementFilter,
  ChangeEditablePictureElementFilterAction,
} from "./actions/changeEditablePictureElementFilter";
import {
  changeEditablePictureElementPicture,
  ChangeEditablePictureElementPictureAction,
} from "./actions/changeEditablePictureElementPicture";
import {
  changeEditableTextElementContent,
  ChangeEditableTextElementContentAction,
} from "./actions/changeEditableTextElementContent";
import {
  changeEditableTextElementFont,
  ChangeEditableTextElementFontAction,
} from "./actions/changeEditableTextElementFont";
import {
  changePageColorDefinition,
  ChangePageColorDefinitionAction,
} from "./actions/changePageColorDefinition";
import {
  changePageFontDefinition,
  ChangePageFontDefinitionAction,
} from "./actions/changePageFontDefinition";
import { changePageOrder, ChangePageOrderAction } from "./actions/changePageOrder";
import {
  changePagePageDefinition,
  ChangePagePageDefinitionAction,
} from "./actions/changePagePageDefinition";
import { changePrintQuantity, ChangePrintQuantityAction } from "./actions/changePrintQuantity";
import { removeNotification, RemoveNotificationAction } from "./actions/removeNotification";
import { removePage, RemovePageAction } from "./actions/removePage";
import { removePicture, RemovePictureAction } from "./actions/removePicture";
import {
  removePictureFromContent,
  RemovePictureFromContentAction,
} from "./actions/removePictureFromContent";
import { removeSelectedPages, RemoveSelectedPagesAction } from "./actions/removeSelectedPages";
import { removeText, RemoveTextAction } from "./actions/removeText";
import {
  setCalendarInitialContent,
  SetCalendarInitialContentAction,
} from "./actions/setCalendarInitialContent";
import { setSavedContent, SetSavedContentAction } from "./actions/setSavedContent";
import {
  swapEditablePictureElementPicture,
  SwapEditablePictureElementPictureAction,
} from "./actions/swapEditablePictureElementPicture";
import { setDraggedPhoto, SetDraggedPhotoAction } from "./actions/ui/setDraggedPhoto";
import { setEditedPicture, SetEditedPictureAction } from "./actions/ui/setEditedPicture";
import { setEventQueue, SetEventQueueAction } from "./actions/ui/setEventQueue";
import { setEditedText, SetEditedTextAction } from "./actions/ui/setEditedText";
import { setGallery, SetGalleryAction } from "./actions/ui/setGallery";
import { setDisplayPreview, SetDisplayPreviewAction } from "./actions/ui/setDisplayPreview";
import { setOrganizePageList, SetOrganizePageListAction } from "./actions/ui/setOrganizePageList";
import {
  setPageConfigMapping,
  SetPageConfigMappingAction,
} from "./actions/ui/setPageConfigMapping";
import { setPageToFocus, SetPageToFocusAction } from "./actions/ui/setPageToFocus";
import { setPanel, SetPanelAction } from "./actions/ui/setPanel";
import { savingForUndo, undoRedo, UndoRedo, UndoRedoAction } from "./actions/undoRedo";
import {
  verifyAndReplacePageDefinitions,
  VerifyAndReplacePageDefinitionsAction,
} from "./actions/verifyAndReplacePageDefinitions";
import { LocalizedContent } from "../../types/customization/text";

export type DraggedPhoto =
  | {
      from: "selection";
      selectionPhoto: SelectionPhoto;
    }
  | {
      from: "editablePicture";
      pageKey: string;
      editablePictureKey: string;
    };

export interface EditedPicture {
  editablePictureKey: string;
  pageKey: string;
}

export interface EditedText {
  elementDefinitionKey: string;
  pageKey: string;
  undoKey: string;
}

export enum Panel {
  CLOSED = "closed",
  EDITCOVER = "editCover",
  EDITPAGE = "editPage",
  EDITTITLE = "editTitle",
  PAGEOPTIONS = "pageOptions",
  OPTIONS = "options",
}

export type OrganizedMode = "deletion" | "ordering";

export type PageConfigMapping = {
  description: LocalizedContent[] | null;
  isForced?: boolean;
  pageKey: string;
};

export interface InitialCustomizationState {
  content: Content;
  template: Template;

  notifications?: NotificationByCode[];
  selection?: SelectionState;
  stash?: {
    selection: Array<{ pageKey: string; selectionPhotoKeys: string[] }>;
  };
  track?: CustomizationTrack;
  ui?: {
    changesToSaveCount?: number;
    defaultColorKey?: string;
    defaultFontKey?: string;
    draggedPhoto?: DraggedPhoto;
    editedPicture?: EditedPicture;
    editedText?: EditedText;
    eventQueue?: Array<() => void>;
    gallery: {
      fixedLayout?: boolean;
      opened: boolean;
      pageKey?: string;
      undoKey?: string;
    };
    organizePageList?: { opened: boolean; mode: OrganizedMode };
    pageKeyToFocus?: string;
    pagesConfigMapping: PageConfigMapping[];
    panel: {
      current: Panel;
      elementDefinitionKey?: string;
      key?: string;
      openedTab?: "photos" | "styles";
    };
    prefilledTextsEdited: Array<{ pageKey: string; elementKey: string }>;
    previewOpened?: boolean;
  };
  undoRedo?: UndoRedo;
}

export type CustomizationState = Required<InitialCustomizationState>;

export type CustomizationAction = { undoKey?: string } & (
  | AddNotificationByCodeAction
  | AddOrUpdateSelectionPhotoAction
  | AddPageAction
  | AddPageAndSwapEditablePictureElementPictureAction
  | AddPictureAction
  | AddPicturesWithAutofillAction
  | ChangeAllPagesPageDefinitionAction
  | ChangeAllPagesColorDefinitionAction
  | ChangeAllPagesFontDefinitionAction
  | ChangeBookNumberAction
  | ChangeBookTitleAction
  | ChangeBookTitleFontKeyAction
  | ChangeEditablePictureElementCroppingAndFilterAction
  | ChangeEditablePictureElementFilterAction
  | ChangeEditablePictureElementPictureAction
  | ChangeEditableTextElementContentAction
  | ChangeEditableTextElementFontAction
  | ChangePageColorDefinitionAction
  | ChangePageFontDefinitionAction
  | ChangePageOrderAction
  | ChangePagePageDefinitionAction
  | ChangePrintQuantityAction
  | RemoveNotificationAction
  | RemovePageAction
  | RemovePictureAction
  | RemovePictureFromContentAction
  | RemoveSelectedPagesAction
  | RemoveTextAction
  | SetCalendarInitialContentAction
  | SetOrganizePageListAction
  | SetPanelAction
  | SetDisplayPreviewAction
  | SetDraggedPhotoAction
  | SetEditedPictureAction
  | SetEditedTextAction
  | SetEventQueueAction
  | SetGalleryAction
  | SetPageConfigMappingAction
  | SetPageToFocusAction
  | SetSavedContentAction
  | SwapEditablePictureElementPictureAction
  | UndoRedoAction
  | VerifyAndReplacePageDefinitionsAction
);

export const reducer: (
  state: CustomizationState,
  action: CustomizationAction
) => CustomizationState = (state, action) => {
  let changesToSave = true;
  let nextState: CustomizationState;
  switch (action.type) {
    case "addNotificationByCode":
      nextState = addNotificationByCode(state, action);
      changesToSave = false;
      break;
    case "addOrUpdateSelectionPhoto":
      nextState = addOrUpdateSelectionPhoto(state, action);
      break;
    case "addPage":
      nextState = addPage(state, action);
      break;
    case "addPageAndSwapEditablePictureElementPicture":
      nextState = addPageAndSwapEditablePictureElementPicture(state, action);
      break;
    case "addPicture":
      nextState = addPicture(state, action);
      break;
    case "addPicturesWithAutofill":
      nextState = addPicturesWithAutofill(state, action);
      break;
    case "changeAllPagesPageDefinition":
      nextState = changeAllPagesPageDefinition(state, action);
      break;
    case "changeAllPagesColorDefinition":
      nextState = changeAllPagesColorDefinition(state, action);
      break;
    case "changeAllPagesFontDefinition":
      nextState = changeAllPagesFontDefinition(state, action);
      break;
    case "changeBookNumber":
      nextState = changeBookNumber(state, action);
      break;
    case "changeBookTitle":
      nextState = changeBookTitle(state, action);
      break;
    case "changeBookTitleFontKey":
      nextState = changeBookTitleFontKey(state, action);
      break;
    case "changeEditablePictureElementCroppingAndFilter":
      nextState = changeEditablePictureElementCroppingAndFilter(state, action);
      break;
    case "changeEditablePictureElementFilter":
      nextState = changeEditablePictureElementFilter(state, action);
      break;
    case "changeEditablePictureElementPicture":
      nextState = changeEditablePictureElementPicture(state, action);
      break;
    case "changeEditableTextElementContent":
      nextState = changeEditableTextElementContent(state, action);
      break;
    case "changeEditableTextElementFont":
      nextState = changeEditableTextElementFont(state, action);
      break;
    case "changePageColorDefinition":
      nextState = changePageColorDefinition(state, action);
      break;
    case "changePageFontDefinition":
      nextState = changePageFontDefinition(state, action);
      break;
    case "changePageOrder":
      nextState = changePageOrder(state, action);
      break;
    case "changePagePageDefinition":
      nextState = changePagePageDefinition(state, action);
      break;
    case "changePrintQuantity":
      nextState = changePrintQuantity(state, action);
      break;
    case "removeNotification":
      nextState = removeNotification(state, action);
      changesToSave = false;
      break;
    case "removePage":
      nextState = removePage(state, action);
      break;
    case "removePicture":
      nextState = removePicture(state, action);
      break;
    case "removePictureFromContent":
      nextState = removePictureFromContent(state, action);
      break;
    case "removeSelectedPages":
      nextState = removeSelectedPages(state, action);
      break;
    case "removeText":
      nextState = removeText(state, action);
      break;
    case "setDisplayPreview":
      nextState = setDisplayPreview(state, action);
      changesToSave = false;
      break;
    case "setDraggedPhoto":
      nextState = setDraggedPhoto(state, action);
      changesToSave = false;
      break;
    case "setCalendarInitialContent":
      nextState = setCalendarInitialContent(state, action);
      changesToSave = false;
      break;
    case "setEditedPicture":
      nextState = setEditedPicture(state, action);
      changesToSave = false;
      break;
    case "setEditedText":
      nextState = setEditedText(state, action);
      changesToSave = false;
      break;
    case "setEventQueue":
      nextState = setEventQueue(state, action);
      changesToSave = false;
      break;
    case "setGallery":
      nextState = setGallery(state, action);
      changesToSave = false;
      break;
    case "setOrganizePageList":
      nextState = setOrganizePageList(state, action);
      changesToSave = false;
      break;
    case "setPageToFocus":
      nextState = setPageToFocus(state, action);
      changesToSave = false;
      break;
    case "setPageConfigMapping":
      nextState = setPageConfigMapping(state, action);
      changesToSave = false;
      break;
    case "setPanel":
      nextState = setPanel(state, action);
      changesToSave = false;
      break;
    case "setSavedContent":
      nextState = setSavedContent(state, action);
      break;
    case "swapEditablePictureElementPicture":
      nextState = swapEditablePictureElementPicture(state, action);
      break;
    case "undoRedo":
      nextState = undoRedo(state, action);
      break;
    case "verifyAndReplacePageDefinitions":
      nextState = verifyAndReplacePageDefinitions(state, action);
      break;
    default: {
      const exhaustiveCheck: never = action;
      throw new Error(`Unsupported type ${(exhaustiveCheck as { type: string }).type}`);
    }
  }

  if (action.type !== "undoRedo") {
    nextState = savingForUndo(state, nextState, action.undoKey);
  }

  nextState = produce(nextState, (draftState) => {
    if (action.type === "setSavedContent") {
      draftState.ui.changesToSaveCount = 0;
    } else if (changesToSave) {
      draftState.ui.changesToSaveCount = (draftState.ui.changesToSaveCount || 0) + 1;
    }
  });

  return nextState;
};

export type DispatchCustomizationAction = Dispatch<CustomizationAction>;
